import React from 'react';
import { isBordo } from '../theme';

// The shared motif: the eight-pointed star that both İznik and Andalusian
// azulejo tiles are built on. One tile repeats seamlessly (corners carry
// quarter stars), so a wall of them reads as a single ceramic surface.
export const TILE_THEMES = {
  cobalt: { bg: '#f4efe6', line: '#1f3a6b', fill: '#1f3a6b', accent: '#b5532f', gold: '#c99a3e' },
  oxblood: { bg: '#6e1a1f', line: '#f4efe6', fill: '#f4efe6', accent: '#d4a24c', gold: '#d4a24c' },
  saffron: { bg: '#d4a24c', line: '#13244a', fill: '#13244a', accent: '#6e1a1f', gold: '#f4efe6' },
  ink: { bg: '#13244a', line: '#d4a24c', fill: '#d4a24c', accent: '#f4efe6', gold: '#d4a24c' },
  aegean: { bg: '#1f4f8f', line: '#f4efe6', fill: '#f4efe6', accent: '#7fb2d9', gold: '#7fb2d9' },
  iznik: { bg: '#f4efe6', line: '#2d7f7c', fill: '#2d7f7c', accent: '#c8443a', gold: '#3fa7a3' },
  lavande: { bg: '#e6dcef', line: '#4b3462', fill: '#4b3462', accent: '#d4a24c', gold: '#8a6bb0' },
  limone: { bg: '#f4efe6', line: '#6f7d3a', fill: '#e3c94f', accent: '#6f7d3a', gold: '#e3c94f' },
  terracotta: { bg: '#a64b2a', line: '#f4efe6', fill: '#f4efe6', accent: '#1f3a6b', gold: '#d4a24c' },
};
// Same tiles in the dark burgundy theme.
const TILE_THEMES_BORDO = {
  cobalt: { bg: '#2a2023', line: '#e7b9bd', fill: '#7b2633', accent: '#d4a24c', gold: '#d4a24c' },
  oxblood: { bg: '#7b2633', line: '#f3eadf', fill: '#f3eadf', accent: '#d4a24c', gold: '#d4a24c' },
  saffron: { bg: '#4a1620', line: '#d4a24c', fill: '#d4a24c', accent: '#e7b9bd', gold: '#e7b9bd' },
  ink: { bg: '#1d1819', line: '#d4a24c', fill: '#d4a24c', accent: '#e7b9bd', gold: '#d4a24c' },
  aegean: { bg: '#1f4f8f', line: '#f3eadf', fill: '#f3eadf', accent: '#7fb2d9', gold: '#7fb2d9' },
  iznik: { bg: '#1d1819', line: '#3fa7a3', fill: '#2d7f7c', accent: '#c8443a', gold: '#3fa7a3' },
  lavande: { bg: '#4b3462', line: '#e9dff0', fill: '#b9a3d6', accent: '#d4a24c', gold: '#e9dff0' },
  limone: { bg: '#2a2023', line: '#e3c94f', fill: '#6f7d3a', accent: '#e3c94f', gold: '#e3c94f' },
  terracotta: { bg: '#a64b2a', line: '#f3eadf', fill: '#f3eadf', accent: '#1f3a6b', gold: '#d4a24c' },
};

// A Mediterranean mix for tile walls: Aegean blue, İznik turquoise, Provence
// lavender, Amalfi lemon, Andalusian terracotta. Scattered, but stable per spot.
const MIX = ['cobalt', 'aegean', 'iznik', 'cobalt', 'lavande', 'oxblood', 'limone', 'cobalt', 'terracotta', 'saffron', 'aegean', 'ink', 'iznik', 'lavande'];
export const mixTheme = (i) => MIX[(Math.imul(i + 7, 2654435761) >>> 0) % MIX.length];

const star = (cx, cy, r) => {
  // Two squares at 45 degrees make the 8-point star.
  const pts = [];
  for (let i = 0; i < 16; i += 1) {
    const a = (Math.PI / 8) * i - Math.PI / 2;
    const rr = i % 2 === 0 ? r : r * 0.72;
    pts.push(`${(cx + rr * Math.cos(a)).toFixed(2)},${(cy + rr * Math.sin(a)).toFixed(2)}`);
  }
  return pts.join(' ');
};

// `light` / `dark` force a palette; otherwise the site theme decides.
export const Tile = ({ theme = 'cobalt', className, light, dark }) => {
  const c = (!light && (dark || isBordo()) ? TILE_THEMES_BORDO : TILE_THEMES)[theme];
  return (
    <svg viewBox="0 0 100 100" className={className} aria-hidden="true" preserveAspectRatio="xMidYMid slice">
      <rect width="100" height="100" fill={c.bg} />
      {/* central star with rosette */}
      <polygon points={star(50, 50, 30)} fill="none" stroke={c.line} strokeWidth="2" />
      <polygon points={star(50, 50, 20)} fill={c.fill} />
      <circle cx="50" cy="50" r="7" fill={c.bg} />
      <circle cx="50" cy="50" r="3" fill={c.accent} />
      {/* four tulip-like petals on the axes */}
      {[0, 90, 180, 270].map((r) => (
        <path key={r} transform={`rotate(${r} 50 50)`} d="M50 8c5 6 5 10 0 14c-5-4-5-8 0-14z" fill={c.accent} />
      ))}
      {/* corner quarter stars so tiles join */}
      {[[0, 0], [100, 0], [0, 100], [100, 100]].map(([x, y]) => (
        <g key={`${x}${y}`}>
          <polygon points={star(x, y, 16)} fill={c.fill} />
          <circle cx={x} cy={y} r="5" fill={c.gold} />
        </g>
      ))}
      {/* edge links */}
      <path d="M22 0v8M78 0v8M22 100v-8M78 100v-8M0 22h8M0 78h8M100 22h-8M100 78h-8" stroke={c.line} strokeWidth="2" />
    </svg>
  );
};

// A large rosette used as a slowly turning ceiling (dome / artesonado).
export const Rosette = ({ className, color = '#d4a24c' }) => (
  <svg viewBox="0 0 400 400" className={className} aria-hidden="true">
    <g fill="none" stroke={color} strokeWidth="1.2">
      {[190, 150, 112, 76].map((r, i) => (
        <polygon key={r} points={star(200, 200, r)} transform={`rotate(${i * 11.25} 200 200)`} opacity={1 - i * 0.15} />
      ))}
      {Array.from({ length: 16 }).map((_, i) => (
        <path key={i} d="M200 200L200 10" transform={`rotate(${i * 22.5} 200 200)`} opacity=".35" />
      ))}
      <circle cx="200" cy="200" r="196" opacity=".5" />
      <circle cx="200" cy="200" r="40" />
    </g>
  </svg>
);

// Line glass with a pour that rises when the scene comes into view.
const S = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.4, strokeLinecap: 'round', strokeLinejoin: 'round' };
const D = (props) => <path pathLength="1" className="ex-draw" {...S} {...props} />;

export const Glass = ({ className }) => (
  <svg viewBox="0 0 160 240" className={className} aria-hidden="true">
    <defs>
      <clipPath id="ex-glass-clip">
        <path d="M34 40h92l-8 110c-2 20-18 32-38 32s-36-12-38-32z" />
      </clipPath>
    </defs>
    <g clipPath="url(#ex-glass-clip)">
      <rect className="ex-pour" x="20" y="40" width="120" height="150" fill="#6e1a1f" />
      <path className="ex-pour-wave" d="M20 118c20-6 40 6 60 0s40-6 60 0v80H20z" fill="#8a2a2e" />
    </g>
    <D d="M34 40h92l-8 110c-2 20-18 32-38 32s-36-12-38-32z" />
    <D d="M80 182v36M52 222h56" />
  </svg>
);
